import { Injectable, computed, signal } from '@angular/core';
import { Kit, flavorPrice, kitPrice } from '../models/kit.model';

export interface CartSelection {
  flavorId: string;
  flavorName: string;
  quantity: number;
  // Preço de cada unidade do produto no kit (o valor cobrado é sempre o da API)
  unitPrice: number;
}

export interface CartItem {
  kit: Kit;
  // Kit com vários produtos: a quantidade de cada produto. Vazio nos outros tipos.
  selections: CartSelection[];
  // Quantidade do item unitário ou do kit unitário (no kit com vários produtos é sempre 1). Ausente = 1.
  quantity?: number;
}

// Limites por linha do carrinho (os mesmos da API)
export const MAX_PRODUCT_QUANTITY = 50;
export const MAX_KIT_ITEM_QUANTITY = 1000;

export const cartItemQuantity = (item: CartItem) => item.quantity ?? 1;

// Preço de um produto do kit agora: a promoção é conferida na hora, por isso some sozinha ao acabar o período
export function selectionUnitPrice(item: CartItem, selection: CartSelection): number {
  const flavor = item.kit.flavors?.find(f => f.id === selection.flavorId);
  return flavor ? flavorPrice(item.kit, flavor) : selection.unitPrice;
}

// Preço normal (sem promoção) de um produto do kit, para mostrar cortado
export function selectionRegularPrice(item: CartItem, selection: CartSelection): number {
  return item.kit.flavors?.find(f => f.id === selection.flavorId)?.price ?? selection.unitPrice;
}

export const cartItemTotal = (item: CartItem) =>
  item.kit.type === 'KIT'
    ? item.selections.reduce((sum, s) => sum + selectionUnitPrice(item, s) * s.quantity, 0)
    : kitPrice(item.kit) * cartItemQuantity(item);

// Itens guardados por versões anteriores da loja (kit sem preço por produto) não são mais aceitos pela API
function isCurrentFormat(item: CartItem): boolean {
  if (!item?.kit?.type) return false;
  if (item.kit.type === 'KIT') {
    return item.selections.length > 0 && item.selections.every(s => typeof s.unitPrice === 'number');
  }
  return item.selections.length === 0;
}

// Um carrinho por loja: os kits de uma empresa não se misturam com os de outra
const CART_KEY = 'cart_items_';

@Injectable({ providedIn: 'root' })
export class CartService {
  private slug = '';

  readonly items = signal<CartItem[]>([]);
  readonly count = computed(() => this.items().length);
  readonly totalAmount = computed(() =>
    this.items().reduce((total, item) => total + cartItemTotal(item), 0)
  );

  // Unidades já no carrinho de um item/kit unitário
  reservedProduct(kitId: string): number {
    return this.items()
      .filter(item => item.kit.id === kitId && item.kit.type !== 'KIT')
      .reduce((sum, item) => sum + cartItemQuantity(item), 0);
  }

  // Unidades já no carrinho de um produto de kit
  reservedFlavor(flavorId: string): number {
    return this.items()
      .flatMap(item => item.selections)
      .filter(selection => selection.flavorId === flavorId)
      .reduce((sum, selection) => sum + selection.quantity, 0);
  }

  // Chamado ao entrar numa loja: carrega o carrinho guardado dela
  useStore(slug: string) {
    if (slug === this.slug) return;
    this.slug = slug;
    this.items.set(this.readStoredCart());
  }

  // Item/kit unitário que já está no carrinho soma na mesma linha; kit com vários produtos entra como uma linha nova
  addToCart(item: CartItem) {
    const index = item.kit.type === 'KIT' ? -1 : this.items().findIndex(i => i.kit.id === item.kit.id);
    if (index < 0) {
      this.update(items => [...items, item]);
      return;
    }
    const current = cartItemQuantity(this.items()[index]);
    this.setItemQuantity(index, Math.min(current + cartItemQuantity(item), MAX_PRODUCT_QUANTITY));
  }

  // Quantidade de um item/kit unitário; zero tira a linha do carrinho
  setItemQuantity(index: number, quantity: number) {
    if (quantity <= 0) {
      this.removeFromCart(index);
      return;
    }
    this.update(items => items.map((item, i) => (i === index ? { ...item, quantity } : item)));
  }

  // Quantidade de um produto dentro de um kit; zero tira o produto, e o kit sai se ficar vazio
  setSelectionQuantity(index: number, flavorId: string, quantity: number) {
    this.update(items =>
      items
        .map((item, i) => {
          if (i !== index) return item;
          const selections = item.selections
            .map(s => (s.flavorId === flavorId ? { ...s, quantity } : s))
            .filter(s => s.quantity > 0);
          return { ...item, selections };
        })
        .filter(item => item.kit.type !== 'KIT' || item.selections.length > 0)
    );
  }

  // Troca os produtos guardados no carrinho pelos dados atuais da loja (preço, promoção, estoque),
  // para o total exibido bater com o que a API vai cobrar
  refreshKits(kits: Kit[]) {
    const byId = new Map(kits.map(kit => [kit.id, kit]));
    if (!this.items().some(item => byId.has(item.kit.id))) return;
    this.update(items => items.map(item => {
      const kit = byId.get(item.kit.id);
      return kit ? { ...item, kit } : item;
    }));
  }

  removeFromCart(index: number) {
    this.update(items => items.filter((_, i) => i !== index));
  }

  clearCart() {
    this.update(() => []);
  }

  // Mantém o carrinho ao recarregar a página (ex.: ida e volta do login)
  private update(change: (items: CartItem[]) => CartItem[]) {
    this.items.update(change);
    if (this.slug) localStorage.setItem(CART_KEY + this.slug, JSON.stringify(this.items()));
  }

  private readStoredCart(): CartItem[] {
    try {
      const stored = JSON.parse(localStorage.getItem(CART_KEY + this.slug) ?? '[]');
      return Array.isArray(stored) ? stored.filter(isCurrentFormat) : [];
    } catch {
      return [];
    }
  }
}
